import useResizeObserver from "@react-hook/resize-observer";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

import {
  Button,
  Callout,
  Loading,
  Modal,
  ModalProps,
  NumericTextInput,
  RichContentDisplay,
  SelectInput,
  SwitchButton,
  Text,
  Toolbar,
  useVolatileCallback,
} from "@examspark/common-ui";
import {
  IconAcceptLine,
  IconDownArrowLine,
  IconPageNextLine,
  IconPagePreviousLine,
  IconTrashLine,
  IconUpArrowLine,
} from "@examspark/common-ui/dist/components/icons";
import { ExamSessionMetadata, IDSK } from "@examspark/types";

import {
  shuffleSessions,
  useExamScoreEditorByAttempts,
} from "../../hooks/useExamScoresByAttempts";
import ItemPreview from "../items/ItemPreview";

const StyledLayout = styled.div`
  display: grid;

  flex: 1;
  min-height: 400px;

  @media (min-width: 800px) {
    grid-template-columns: 1fr 300px;
    grid-template-rows: 50px 1fr;
    grid-template-areas:
      "question session"
      "player   session";
  }

  @media (max-width: 800px) {
    grid-template-areas:
      "session"
      "question"
      "player";

    grid-template-rows: min-content min-content 1fr;
  }
`;

const StyledQuestion = styled.div`
  grid-area: question;
  padding: 1rem;
`;

const StyledSessions = styled.div`
  grid-area: session;
  padding: 1rem;

  @media (max-width: 800px) {
    border-bottom: solid 1px #ddd;
  }

  @media (min-width: 800px) {
    border-left: solid 1px #ddd;
  }

  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledPlayer = styled.div`
  grid-area: player;
  overflow: auto;

  // ensure the scrollable player area is at least a usable size
  min-height: 400px;

  display: flex;
  flex-direction: column;
`;

const StyledPreviewWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

const StyledSelectWrapper = styled.div`
  flex: 1;

  min-width: 100px;
`;

const StyledLoadingLock = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledScoreInput = styled(NumericTextInput)`
  width: 4ch;
`;

const StyledRubric = styled.div`
  padding: 1rem;
`;

/*
    Important Information for understanding the following code:

    We materialize the scores into two places:
      1. The exam-session itself, in the `examSessionResults` attribute.
      2. the exam-response#{item} record.

    When a teacher enters a score, we update the exam-response record.

    When a teacher finalizes the score, we mark the session as completeNeedsUpdate
    and a background process will update the examSessionResults record as well
    as a ton of other bookkeeping.

    The goal is to TRY to hide the background process from the user
    because the 2-save-button approach caused a ton of confusion.
    So instead we keep track of the touched sessions and handle the
    save in the background.
  */
const EditExamScoresModal = (
  props: ModalProps & {
    readOnly?: boolean;

    // this is called only if there were changes to the scores
    onSave?: () => void;

    anonymizeStudents?: boolean;
    showAttempts: boolean;

    // initialize via the useExamScoreEditor hook. This controls
    // all data fetching and saving.
    examScoreController: ReturnType<typeof useExamScoreEditorByAttempts>;
  },
) => {
  const [playerMode, setPlayerMode] = useState<"response" | "key">("response");

  const {
    selectedQuestionId,
    setSelectedQuestionId,
    selectedSessionId,
    setSelectedSessionId,
    selectedAttemptId,
    setSelectedAttemptId,

    questions,
    sessions,
    sessionAttempts,
    sessionAttemptBlobs,

    isSaving,
    setIsSaving,

    score,
    setScore,

    examConfig,

    question,
    session,
    sessionAttempt,

    examSessionItemResult,
    examSessionItemResponse,
    missingScoresPerQuestion,

    saveScore,
    clearScore,
    saveScoreOnNavigate,
    finalizeScores,
  } = props.examScoreController;

  const sessionAttemptItemScore =
    sessionAttempt?.examSessionResults?.items.find(
      (r) => r.id === selectedQuestionId?.id && r.sk === selectedQuestionId?.sk,
    );

  const currentSessionAttempts =
    sessionAttempts
      ?.filter((a) => a.examSessionId === selectedSessionId?.id)
      .sort((a, b) => b.attemptNumber - a.attemptNumber) ?? [];

  // if asked to anonymize students, also (deterministically)
  // randomize the student order
  // This sort order SHOULD match the order of everyone
  // who is showing a result table so that it is consistent
  // inside and outside rapid reviewer
  const sortedSessions = props.anonymizeStudents
    ? shuffleSessions(examConfig?.id, sessions ?? [])
    : sessions;

  // track when to show the borderless modal
  const [modalIsFull, setModalIsFull] = useState(false);
  useResizeObserver(window.document.body, (entry) => {
    setModalIsFull(entry.contentRect.width > 800);
  });

  const handleAfterOpen = async () => {
    setSelectedQuestionId(null);
    setSelectedSessionId(null);
    setSelectedAttemptId(null);

    props.onAfterOpen?.();
  };

  const stabilizedOnSave = useVolatileCallback(props.onSave);
  const stabilizedOnRequestClose = useVolatileCallback(props.onRequestClose);

  const handleRequestClose = useCallback(async () => {
    // this controls setIsSaving's itself, so it happens before the setIsSaving
    // call below
    await saveScoreOnNavigate();

    setIsSaving(true);
    const hadChanges = await finalizeScores();

    stabilizedOnRequestClose?.();

    setSelectedQuestionId(null);
    setSelectedSessionId(null);
    setSelectedAttemptId(null);

    if (hadChanges) {
      stabilizedOnSave?.();
    }

    setIsSaving(false);
  }, [
    saveScoreOnNavigate,
    setIsSaving,
    finalizeScores,
    setSelectedQuestionId,
    setSelectedSessionId,
    setSelectedAttemptId,
    stabilizedOnRequestClose,
    stabilizedOnSave,
  ]);

  const sessionMetadata: ExamSessionMetadata = {
    shuffleSeed: session?.id ?? null,
    shuffleOptions: false,
    /**
     * Shuffling options in rapid reviewer, IMO, leads to an inconsistent
     * reviewing experience for teachers toggling students for a given response.
     * Deciding to leave options normalized for now, maybe with an option to
     * toggle in the reviewer for the teacher to see exactly how the options
     * were arranged for a given student. If this never gets implemented, this
     * `sessionMetadata` can get deleted. */
    // shuffleOptions: !!examConfig.config?.shuffleOptions,
    shuffleItems: false,
  };

  return (
    <Modal
      title="Modify Scores"
      size={modalIsFull ? "full" : "max"}
      {...props}
      flexBodyColumn
      onAfterOpen={handleAfterOpen}
      onRequestClose={handleRequestClose}
    >
      <StyledLayout>
        {isSaving && (
          <StyledLoadingLock>
            <Loading />
          </StyledLoadingLock>
        )}
        <StyledSessions>
          <Toolbar>
            <StyledSelectWrapper>
              <SelectInput
                value={getResourceId(selectedSessionId)}
                disabled={!sessions}
                placeholder={sessions ? "Select Session" : "Loading..."}
                options={sortedSessions?.map((s, idx) => ({
                  value: getResourceId(s),
                  label:
                    props.anonymizeStudents || !s.name
                      ? `Student: ${idx + 1}`
                      : s.name,
                }))}
                onChange={async (value) => {
                  if (!value) {
                    setSelectedSessionId(null);
                    return;
                  }
                  const [id, sk] = value.value.split("/");
                  if (!id || !sk) return;

                  setSelectedSessionId({ id, sk });

                  await saveScoreOnNavigate();
                }}
              />
            </StyledSelectWrapper>
            <Button
              title="Previous Session"
              disabled={!sessions || !selectedSessionId}
              onClick={async () => {
                const prevSessionId = previousIDSK(
                  selectedSessionId,
                  sortedSessions,
                );
                if (!prevSessionId) return;

                await saveScoreOnNavigate();

                setSelectedSessionId(prevSessionId);
              }}
            >
              <IconPagePreviousLine width={"12px"} />
            </Button>
            <Button
              title="Next Session"
              disabled={!sessions || !selectedSessionId}
              onClick={async () => {
                const nextSessionId = nextIDSK(
                  selectedSessionId,
                  sortedSessions,
                );
                if (!nextSessionId) return;

                await saveScoreOnNavigate();

                setSelectedSessionId(nextSessionId);
              }}
            >
              <IconPageNextLine width={"12px"} />
            </Button>
          </Toolbar>

          {session && session?.maturity !== "COMPLETE" && (
            <>
              <Callout>
                This session is still in progress. Scores cannot be edited.
              </Callout>
            </>
          )}
          {session && question && props.showAttempts && (
            <>
              <SelectInput
                value={getResourceId(selectedAttemptId)}
                disabled={!currentSessionAttempts.length}
                placeholder={
                  currentSessionAttempts.length
                    ? "Select Attempt"
                    : "Loading..."
                }
                options={currentSessionAttempts.map((a) => ({
                  value: getResourceId(a),
                  label: `Attempt ${a.attemptNumber}`,
                }))}
                onChange={async (value) => {
                  console.log({ value });
                  if (!value) {
                    setSelectedAttemptId(null);
                    return;
                  }
                  // since the SK of the attempt already contains a '/', we need to be a little
                  // clever
                  const [id, ...skParts] = value.value.split("/");
                  const sk = skParts.join("/");
                  if (!id || !sk) return;

                  setSelectedAttemptId({ id, sk });

                  // await saveScoreOnNavigate();
                }}
              />
            </>
          )}
          {session && question && (
            <Toolbar>
              <span>Scored a</span>
              <StyledScoreInput
                value={
                  score ??
                  // sessionResponse?.scoreActual ??
                  sessionAttemptItemScore?.score?.actual ??
                  ""
                }
                onChange={(e) => setScore(e.target.value)}
                disabled={props.readOnly || session?.maturity !== "COMPLETE"}
              />
              <span>
                out of {examSessionItemResult?.score.max ?? question.scoreMax}
              </span>
              &nbsp;
              <Button
                title="Save Score"
                color="primary"
                disabled={
                  props.readOnly ||
                  score == null ||
                  session?.maturity !== "COMPLETE"
                }
                onClick={saveScore}
              >
                <IconAcceptLine width={"12px"} />
              </Button>
              <Button
                title="Clear Score"
                color="danger"
                disabled={
                  props.readOnly ||
                  // sessionResponse?.scoreActual == null ||
                  sessionAttemptItemScore?.score?.actual == null ||
                  session.maturity !== "COMPLETE"
                }
                onClick={clearScore}
              >
                <IconTrashLine width={"12px"} />
              </Button>
            </Toolbar>
          )}

          {question?.content?.canvas?.find(
            (canvas) => canvas?.scoring?.rubric,
          ) && (
            <div>
              <Text.H3>Rubric</Text.H3>
              <StyledRubric>
                {question?.content?.canvas?.map((canvas) => (
                  <RichContentDisplay
                    key={canvas.id}
                    content={canvas?.scoring?.rubric?.body}
                  />
                ))}
              </StyledRubric>
            </div>
          )}
        </StyledSessions>
        <StyledQuestion>
          <Toolbar>
            <StyledSelectWrapper>
              <SelectInput
                value={getResourceId(selectedQuestionId)}
                disabled={!questions}
                placeholder={questions ? "Select Question" : "Loading..."}
                options={questions?.map((q) => ({
                  value: getResourceId(q),
                  label: q.name ?? "",
                  badgeCount: missingScoresPerQuestion?.[getResourceId(q)],
                }))}
                useBadgeForOptions
                onChange={async (value) => {
                  if (!value) {
                    setSelectedQuestionId(null);
                    return;
                  }
                  const [id, sk] = value.value.split("/");
                  if (!id || !sk) return;

                  setSelectedQuestionId({ id, sk });

                  await saveScoreOnNavigate();
                }}
              />
            </StyledSelectWrapper>
            <Button
              title="Previous Question"
              disabled={!questions || !selectedQuestionId}
              onClick={async () => {
                const prevQuestionId = previousIDSK(
                  selectedQuestionId,
                  questions,
                );
                if (!prevQuestionId) return;

                await saveScoreOnNavigate();

                setSelectedQuestionId(prevQuestionId);
              }}
            >
              <IconUpArrowLine width={"12px"} />
            </Button>
            <Button
              title="Next Question"
              disabled={!questions || !selectedQuestionId}
              onClick={async () => {
                const nextQuestionId = nextIDSK(selectedQuestionId, questions);
                if (!nextQuestionId) return;

                await saveScoreOnNavigate();

                setSelectedQuestionId(nextQuestionId);
              }}
            >
              <IconDownArrowLine width={"12px"} />
            </Button>
          </Toolbar>
        </StyledQuestion>
        <StyledPlayer>
          {question && (
            <>
              <Toolbar>
                <Toolbar.Right>
                  <SwitchButton
                    label="Show Key"
                    checked={playerMode === "key"}
                    onClick={() =>
                      setPlayerMode((mode) =>
                        mode === "response" ? "key" : "response",
                      )
                    }
                  />
                </Toolbar.Right>
              </Toolbar>
              <StyledPreviewWrapper>
                <ItemPreview
                  data={question}
                  condensed
                  hideToolbar
                  scoreStudentResponse
                  isLocked
                  sessionData={sessionMetadata}
                  showKey={playerMode === "key"}
                  response={examSessionItemResponse}
                  blobs={sessionAttemptBlobs?.[question.id]}
                />
              </StyledPreviewWrapper>
            </>
          )}
        </StyledPlayer>
      </StyledLayout>
    </Modal>
  );
};
export default EditExamScoresModal;

// helper methods
const previousIDSK = (current: IDSK | null, items: IDSK[] | null) => {
  if (!items) return;
  if (!current) return;

  const currentIndex = items.findIndex(
    (item) => item.id === current.id && item.sk === current.sk,
  );
  if (currentIndex === -1 || currentIndex === 0) return null;

  const prevItem = items.at(currentIndex - 1);
  if (!prevItem) return null;

  return {
    id: prevItem.id,
    sk: prevItem.sk,
  } as IDSK;
};

const nextIDSK = (current: IDSK | null, items: IDSK[] | null) => {
  if (!items) return;
  if (!current) return;

  const currentIndex = items.findIndex(
    (item) => item.id === current.id && item.sk === current.sk,
  );
  if (currentIndex === -1) return null;

  const nextItem = items.at(currentIndex + 1);
  if (!nextItem) return null;

  return {
    id: nextItem.id,
    sk: nextItem.sk,
  } as IDSK;
};

function getResourceId(idsk: IDSK): string;
function getResourceId(idsk: null): null;
function getResourceId(idsk: IDSK | null): string | null;
function getResourceId(idsk: IDSK | null): string | null {
  if (!idsk) return null;
  return [idsk.id, idsk.sk].join("/");
}
